import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'brand-logos';
const SIGN_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days — refreshed on demand

type Cached = { url: string; expiresAt: number };
const cache = new Map<string, Cached>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Extract the object path (inside the brand-logos bucket) from any of the
 * URL shapes we've stored historically:
 *   - https://<ref>.supabase.co/storage/v1/object/public/brand-logos/<path>
 *   - https://<ref>.supabase.co/storage/v1/object/sign/brand-logos/<path>?token=...
 *   - https://<ref>.supabase.co/storage/v1/object/authenticated/brand-logos/<path>
 *   - brand-logos/<path>
 */
export function extractBrandLogoPath(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;
  const m = s.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/brand-logos\/([^?#]+)/);
  if (m) return decodeURIComponent(m[1]);
  if (s.startsWith(`${BUCKET}/`)) return s.slice(BUCKET.length + 1);
  return null; // external URL — passthrough
}

export function isBrandLogoStorageUrl(input: string | null | undefined): boolean {
  return !!extractBrandLogoPath(input);
}

/**
 * Resolve a stored brand logo URL into a currently-valid renderable URL.
 * - Storage-backed values → mint a signed URL (cached, self-heals).
 * - External URLs → passthrough (Supabase can't sign them).
 * - Empty → null so the caller falls back to initials.
 */
export async function resolveBrandLogoUrl(
  input: string | null | undefined,
  opts: { forceRefresh?: boolean } = {},
): Promise<string | null> {
  if (!input) return null;
  const path = extractBrandLogoPath(input);
  if (!path) return input; // external URL — pass through
  const now = Date.now();
  if (opts.forceRefresh) {
    cache.delete(path);
    inflight.delete(path);
  }
  const hit = cache.get(path);
  if (hit && hit.expiresAt - now > 5 * 60_000) return hit.url;
  if (inflight.has(path)) return inflight.get(path)!;
  const p = (async () => {
    try {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGN_TTL_SECONDS);
      if (error || !data?.signedUrl) return null;
      cache.set(path, { url: data.signedUrl, expiresAt: now + SIGN_TTL_SECONDS * 1000 });
      return data.signedUrl;
    } catch {
      return null;
    } finally {
      inflight.delete(path);
    }
  })();
  inflight.set(path, p);
  return p;
}

export function invalidateBrandLogoCache(input?: string | null) {
  if (!input) { cache.clear(); inflight.clear(); return; }
  const path = extractBrandLogoPath(input);
  if (path) { cache.delete(path); inflight.delete(path); }
}

export function brandInitials(name?: string | null): string {
  if (!name) return 'B';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Health probe for the audit page. */
export async function probeBrandLogo(rawUrl: string | null | undefined): Promise<{
  hasUrl: boolean;
  path: string | null;
  resolved: string | null;
  reachable: boolean;
  storageExists: boolean | null;
  external: boolean;
}> {
  if (!rawUrl) return { hasUrl: false, path: null, resolved: null, reachable: false, storageExists: null, external: false };
  const path = extractBrandLogoPath(rawUrl);
  const external = !path;
  let storageExists: boolean | null = null;
  if (path) {
    try {
      const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
      const file = path.slice(parent.length + (parent ? 1 : 0));
      const { data } = await supabase.storage.from(BUCKET).list(parent, { search: file, limit: 1 });
      storageExists = !!data?.some((o) => o.name === file);
    } catch {
      storageExists = null;
    }
  }
  const resolved = await resolveBrandLogoUrl(rawUrl);
  let reachable = false;
  if (resolved) {
    try {
      const r = await fetch(resolved, { method: 'HEAD' });
      reachable = r.ok;
    } catch {
      reachable = false;
    }
  }
  return { hasUrl: true, path, resolved, reachable, storageExists, external };
}
